import { apolloClient } from "@/apollo";
import AddMetadatFieldMutation from "@/apollo/mutations/v2/AddMetadataField.gql";
import RemoveMetadatFieldMutation from "@/apollo/mutations/v2/RemoveMetadataField.gql";
import AddComputedDeliverableMetadataValueMutation from "@/apollo/mutations/v2/AddComputedDeliverableMetadataValue.gql";
import AddComputedDeliverableMetadataValueUsingFiltersMutation from "@/apollo/mutations/v2/AddComputedDeliverableMetadataValueUsingFilters.gql";
import RemoveMetadataMutation from "@/apollo/mutations/v2/RemoveMetadata.gql";
import RemoveMetadataUsingFilterMutation from "@/apollo/mutations/v2/RemoveMetadataUsingFilter.gql";
import UpdateMetadatFieldMutation from "@/apollo/mutations/v2/UpdateMetadataField.gql";
import MetadataFieldsQuery from "@/apollo/queries/v2/MetadataFields.gql";
import type { ConstructedLibraryFilters } from "@/utils/constructLibraryFiltersQueryVariables";

type ComputedDeliverableIdentifier = {
    editableGroupValueIds: string[];
    language: string;
    masterTemplateId: string;
};

type MetadataField = {
    id: string;
    name: string;
    campaignId?: string;
};

type MetadataFieldsQueryResult = {
    metadataFields: MetadataField[];
};

type AddMetadataFieldResult = {
    addMetadataField: MetadataField;
};

type RemoveMetadataFieldResult = {
    removeMetadata: unknown;
};

type AddMetadataValueResult = {
    addMetadataValue: {
        __typename: "MetadataValue";
        _id: string;
        field: {
            _id: string;
            name: string;
        };
        value: string;
    };
};

export async function addMetadataField(campaignId: string, name: string) {
    const { data } = await apolloClient.mutate<AddMetadataFieldResult>({
        mutation: AddMetadatFieldMutation,
        fetchPolicy: "no-cache",
        variables: {
            campaignId,
            name
        }
    });

    return data?.addMetadataField;
}

export async function getMetadataFieldsOfCampaign(campaignId: string) {
    const { data } = await apolloClient.query<MetadataFieldsQueryResult>({
        fetchPolicy: "network-only",
        query: MetadataFieldsQuery,
        variables: {
            campaignId
        }
    });

    return data.metadataFields;
}

export async function removeMetadataField(fieldId: string) {
    await apolloClient.mutate({
        mutation: RemoveMetadatFieldMutation,
        variables: {
            fieldId
        }
    });

    return fieldId;
}

export async function addComputedDeliverablesMetadataValue(
    computedDeliverableIdentifiers: ComputedDeliverableIdentifier[],
    fieldId: string,
    value: string
) {
    const response = await apolloClient.mutate<AddMetadataValueResult>({
        mutation: AddComputedDeliverableMetadataValueMutation,
        fetchPolicy: "no-cache",
        variables: {
            computedDeliverableIdentifiers,
            fieldId,
            value
        }
    });

    return response.data?.addMetadataValue;
}

export async function addComputedDeliverablesMetadataValueUsingFilter(
    libraryFilters: ConstructedLibraryFilters,
    fieldId: string,
    value: string
) {
    const { data } = await apolloClient.mutate<AddMetadataValueResult>({
        mutation: AddComputedDeliverableMetadataValueUsingFiltersMutation,
        fetchPolicy: "no-cache",
        variables: {
            libraryFilters,
            fieldId,
            value
        }
    });

    return data?.addMetadataValue;
}

export async function removeComputedDeliverablesMetadataValueByFieldId(
    computedDeliverableIdentifiers: ComputedDeliverableIdentifier[],
    fieldId: string
) {
    await apolloClient.mutate({
        mutation: RemoveMetadataMutation,
        variables: {
            computedDeliverableIdentifiers,
            fieldId
        }
    });
}

export async function removeComputedDeliverablesMetadataValueUsingFilter(
    libraryFilters: ConstructedLibraryFilters,
    fieldId: string
) {
    const response = await apolloClient.mutate<RemoveMetadataFieldResult>({
        mutation: RemoveMetadataUsingFilterMutation,
        variables: {
            libraryFilters,
            fieldId
        }
    });

    return response.data?.removeMetadata;
}

export async function updateMetadataField(fieldId: string, name: string) {
    const response = await apolloClient.mutate<{ updateMetadataField: MetadataField }>({
        mutation: UpdateMetadatFieldMutation,
        variables: {
            fieldId,
            name
        }
    });

    return response.data?.updateMetadataField;
}
